import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Phone, Smartphone, Copy, Mail, Contact } from 'lucide-react';
import { useI18n } from '../utils/i18n';
import { useToast } from './ToastProvider';
import Modal from './ui/Modal';
import { telHref } from '../utils/telefon';
import { getKontaktVerzeichnis } from '../utils/kontaktVerzeichnis';
import { kundePasstZurSuche, ansprechpartnerPasstZurSuche } from '../utils/suchFilter';
import './KontaktSuche.css';

// Kontaktsuche in der Kopfleiste (#255, Feedback 32 von C.Firchau):
// Telefonnummern und Kundendaten nachschlagen, ohne die Seite zu wechseln.
// Das Ergebnis steht als Kasten unter dem Feld, jede Nummer ist ein
// Anruf-Link (tel:, z. B. über Teams) mit Kopier-Knopf. Durchsucht werden
// Kunden UND Ansprechpartner (Hinweis C.Firchau 11.09.: „als Kontaktsuche
// kennzeichnen, nicht als Kundensuche“). Mobil öffnet ein Knopf denselben
// Inhalt als Dialog.
//
// Sichtbar nur bei aktivem Kundenstamm und für Manager/Admin -- die Nummern
// kommen aus der Verwaltungs-Route (siehe utils/kontaktVerzeichnis).

const MIN_ZEICHEN = 2;
const MAX_KUNDEN = 8;
const MAX_ANSPRECHPARTNER = 4;

// Treffer aufbereiten. Ein Ansprechpartner gilt als „passend“, wenn eines der
// Suchwörter bei ihm selbst vorkommt -- „Müller Anna“ soll bei Kunde Müller
// die Anna nach oben holen, obwohl „Müller“ nicht in ihrem Namen steht.
// Passt keiner, stehen die Ansprechpartner des Kunden in ihrer Reihenfolge da.
export function sucheKontakte(kunden, begriff) {
  const woerter = String(begriff || '').trim().split(/\s+/).filter(Boolean);
  const treffer = (kunden || []).filter(k => kundePasstZurSuche(k, begriff));
  const liste = treffer.slice(0, MAX_KUNDEN).map(kunde => {
    const alle = kunde.contacts || [];
    const passende = alle.filter(c => woerter.some(w => ansprechpartnerPasstZurSuche(c, w)));
    const auswahl = passende.length > 0 ? passende : alle;
    return {
      kunde,
      ansprechpartner: auswahl.slice(0, MAX_ANSPRECHPARTNER),
      weitere: Math.max(0, auswahl.length - MAX_ANSPRECHPARTNER)
    };
  });
  return { liste, gesamt: treffer.length };
}

const KontaktSuche = ({ isMobile = false }) => {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const navigate = useNavigate();
  const [begriff, setBegriff] = useState('');
  const [kunden, setKunden] = useState(null); // null = noch nicht geladen
  const [offen, setOffen] = useState(false);
  const [dialogOffen, setDialogOffen] = useState(false);
  const wurzelRef = useRef(null);

  const laden = () => {
    if (kunden === null) getKontaktVerzeichnis().then(setKunden);
  };

  // Außerhalb klicken schließt den Kasten -- click UND contextmenu in der
  // Capture-Phase (#189, sonst bleibt er bei einem Rechtsklick daneben stehen).
  useEffect(() => {
    if (!offen) return undefined;
    const schliessen = (e) => {
      if (wurzelRef.current && !wurzelRef.current.contains(e.target)) setOffen(false);
    };
    document.addEventListener('click', schliessen, true);
    document.addEventListener('contextmenu', schliessen, true);
    return () => {
      document.removeEventListener('click', schliessen, true);
      document.removeEventListener('contextmenu', schliessen, true);
    };
  }, [offen]);

  const ergebnis = useMemo(
    () => (begriff.trim().length >= MIN_ZEICHEN && kunden ? sucheKontakte(kunden, begriff) : null),
    [kunden, begriff]
  );

  const kopieren = (nummer) => {
    try {
      Promise.resolve(navigator.clipboard?.writeText(nummer))
        .then(() => pushToast({ message: t('customerField.numberCopied', 'Nummer kopiert.'), type: 'success' }))
        .catch(() => {});
    } catch (_) { /* Zwischenablage nicht verfügbar -- der Anruf-Link bleibt */ }
  };

  const nummer = (wert, Symbol, schluessel) => (
    <span className="kontaktsuche-nummer" key={schluessel}>
      <a
        href={telHref(wert)}
        title={t('customerField.callTitle', 'Anrufen — Rechtsklick kopiert die Nummer')}
        onContextMenu={(e) => { e.preventDefault(); kopieren(wert); }}
      >
        <Symbol size={13} aria-hidden="true" />
        {wert}
      </a>
      <button
        type="button"
        className="kontaktsuche-kopieren"
        onClick={() => kopieren(wert)}
        title={t('customerField.copyNumber', 'Nummer kopieren')}
        aria-label={`${t('customerField.copyNumber', 'Nummer kopieren')}: ${wert}`}
      >
        <Copy size={12} aria-hidden="true" />
      </button>
    </span>
  );

  const zurVerwaltung = () => {
    setOffen(false);
    setDialogOffen(false);
    navigate('/customers');
  };

  const inhalt = () => {
    if (begriff.trim().length < MIN_ZEICHEN) {
      return <div className="kontaktsuche-hinweis">{t('contactSearch.minChars', 'Mindestens zwei Zeichen eingeben.')}</div>;
    }
    if (kunden === null) {
      return <div className="kontaktsuche-hinweis" role="status">{t('contactSearch.loading', 'Kontakte werden geladen…')}</div>;
    }
    if (!ergebnis || ergebnis.gesamt === 0) {
      return <div className="kontaktsuche-hinweis" role="status">{t('contactSearch.none', 'Keine Treffer.')}</div>;
    }
    return (
      <>
        {ergebnis.liste.map(({ kunde, ansprechpartner, weitere }) => (
          <div className="kontaktsuche-karte" key={kunde.id}>
            <div>
              <span className="kontaktsuche-name">{kunde.name}</span>
              {kunde.shortName && kunde.shortName !== kunde.name && (
                <span className="kontaktsuche-kurz"> · {kunde.shortName}</span>
              )}
            </div>
            {[kunde.phone1, kunde.phone2, kunde.phone3].some(Boolean) && (
              <div className="kontaktsuche-nummern">
                {[kunde.phone1, kunde.phone2, kunde.phone3].filter(Boolean).map((n, i) => nummer(n, Phone, `k${i}`))}
              </div>
            )}
            {(kunde.domains || []).length > 0 && (
              <div className="kontaktsuche-domaenen">{kunde.domains.join(', ')}</div>
            )}
            {ansprechpartner.map(c => (
              <div className="kontaktsuche-ansprechpartner" key={c.id}>
                <div className="kontaktsuche-person">{c.name}</div>
                <div className="kontaktsuche-nummern">
                  {c.phoneMobile && nummer(c.phoneMobile, Smartphone, 'm')}
                  {c.phoneLandline && nummer(c.phoneLandline, Phone, 'f')}
                  {c.email && (
                    <span className="kontaktsuche-nummer">
                      <a href={`mailto:${c.email}`}><Mail size={13} aria-hidden="true" />{c.email}</a>
                    </span>
                  )}
                </div>
              </div>
            ))}
            {weitere > 0 && (
              <div className="kontaktsuche-mehr">{t('contactSearch.moreContacts', '+{{count}} weitere Ansprechpartner', { count: weitere })}</div>
            )}
          </div>
        ))}
        {ergebnis.gesamt > ergebnis.liste.length && (
          <div className="kontaktsuche-hinweis">
            {t('contactSearch.moreCustomers', '{{count}} weitere Treffer — Suche verfeinern', { count: ergebnis.gesamt - ergebnis.liste.length })}
          </div>
        )}
        <button type="button" className="kontaktsuche-verwaltung" onClick={zurVerwaltung}>
          {t('contactSearch.openAdmin', 'In der Kundenverwaltung öffnen')}
        </button>
      </>
    );
  };

  const feldProps = {
    type: 'search',
    value: begriff,
    placeholder: t('contactSearch.placeholder', 'Kontaktsuche…'),
    'aria-label': t('contactSearch.label', 'Kontaktsuche: Kunden und Ansprechpartner'),
    onChange: (e) => { setBegriff(e.target.value); setOffen(true); laden(); }
  };

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          className="kontaktsuche-knopf"
          onClick={() => { laden(); setDialogOffen(true); }}
          title={t('contactSearch.title', 'Kontaktsuche')}
          aria-label={t('contactSearch.title', 'Kontaktsuche')}
        >
          <Contact size={18} color="white" aria-hidden="true" />
        </button>
        <Modal open={dialogOffen} onClose={() => setDialogOffen(false)} title={t('contactSearch.title', 'Kontaktsuche')} maxWidth="480px">
          <div className="kontaktsuche-dialog">
            <input {...feldProps} autoFocus />
            {inhalt()}
          </div>
        </Modal>
      </>
    );
  }

  return (
    <div className="kontaktsuche" ref={wurzelRef}>
      <label className="kontaktsuche-feld">
        <Search size={14} color="white" aria-hidden="true" />
        <input
          {...feldProps}
          onFocus={() => { laden(); setOffen(true); }}
          onKeyDown={(e) => { if (e.key === 'Escape') { setOffen(false); e.currentTarget.blur(); } }}
        />
      </label>
      {offen && begriff.trim().length >= MIN_ZEICHEN && (
        <div className="kontaktsuche-kasten" role="region" aria-label={t('contactSearch.results', 'Treffer der Kontaktsuche')}>
          {inhalt()}
        </div>
      )}
    </div>
  );
};

export default KontaktSuche;
